/**
 * Canonical settings row: title and description on the left, the control
 * right-aligned (DESIGN-SYSTEM.md "Setting rows, not forms"). Rows commit on their
 * own — toggles/selects on change, text inputs on blur — and failures render
 * inline on the row, never at the page top.
 *
 * A row's extra explanation hangs off the row TITLE, exactly as everywhere
 * else in the app — there is no separate info glyph to find (the old `InfoTip`
 * is gone). The tooltip text obeys the one-sentence rule in DESIGN.md.
 */
import { Toggle } from "./Toggle";
import { Tooltip } from "./Tooltip";
import { Select, type SelectOption } from "./Select";
import { cx } from "@shared/cx";
import formStyles from "./field.module.css";
import { useSettingCommit } from "../../lib/use-setting-commit";
import { InlineBanner } from "./InlineBanner";
import styles from "./SettingRow.module.css";

export function SettingRow({
  title,
  description,
  tooltip,
  htmlFor,
  labelId,
  optional = false,
  error,
  indent = false,
  children,
}: {
  title: string;
  description?: string;
  /** One sentence explaining what the setting affects; hangs off the title. */
  tooltip?: string;
  htmlFor?: string;
  labelId?: string;
  optional?: boolean;
  error?: string | null;
  /** Dependent setting: indented under its parent row. */
  indent?: boolean;
  children: React.ReactNode;
}) {
  const optionalMark = optional ? (
    <span className={styles.optional}> (optional)</span>
  ) : null;
  // The title itself is the tooltip trigger — no icon stands in for it. The
  // Tooltip wraps the title element rather than sitting inside it: Astryx keeps
  // the (closed) bubble in the DOM, and inside a <label> its text would join
  // the control's accessible name.
  const titleEl = htmlFor ? (
    <label id={labelId} htmlFor={htmlFor} className={styles.title}>
      {title}
      {optionalMark}
    </label>
  ) : (
    // A span, not a <p>: Astryx renders the tooltip bubble as a div in place,
    // which would close a <p> early — so the span carries the block itself.
    <span id={labelId} className={cx(styles.title, styles.titleBlock)}>
      {title}
      {optionalMark}
    </span>
  );
  return (
    <div className={cx(styles.row, indent && styles.rowIndented)}>
      <div className={styles.text}>
        {/* The trigger has to be inline-block: see `.tooltipTrigger` in the
            module for why an inline span around a block title mispositions
            the bubble (#346). */}
        {tooltip ? (
          <Tooltip content={tooltip} className={styles.tooltipTrigger}>
            {titleEl}
          </Tooltip>
        ) : (
          titleEl
        )}
        {description && <p className={styles.description}>{description}</p>}
        {error && <InlineBanner variant="error">{error}</InlineBanner>}
      </div>
      <div className={styles.control}>{children}</div>
    </div>
  );
}

/**
 * Text setting committed on blur (or Enter). An unchanged or — for required
 * rows — emptied value never fires a save; required rows revert instead.
 */
export function TextSettingRow({
  id,
  title,
  description,
  tooltip,
  value,
  onSave,
  type = "text",
  optional = false,
  required = false,
  indent = false,
  controlWidth = styles.textControl,
}: {
  id: string;
  title: string;
  description?: string;
  tooltip?: string;
  value: string;
  onSave: (next: string) => Promise<void>;
  type?: string;
  optional?: boolean;
  required?: boolean;
  indent?: boolean;
  /** A module class for the input's width; the default is the row's own. */
  controlWidth?: string;
}) {
  const { draft, setDraft, onFocus, saving, error, commitDraft, resetDraft } =
    useSettingCommit(value, onSave);

  const commit = () => {
    // Required rows revert an emptied value instead of saving it.
    if (required && draft.trim() === "") return resetDraft();
    void commitDraft();
  };

  return (
    <SettingRow
      title={title}
      description={description}
      tooltip={tooltip}
      htmlFor={id}
      optional={optional}
      error={error}
      indent={indent}
    >
      <input
        id={id}
        type={type}
        className={cx(formStyles.input, controlWidth)}
        value={draft}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={onFocus}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
      />
    </SettingRow>
  );
}

/**
 * Toggle setting: flipping IS the commit; on failure the row shows why.
 *
 * `disabled` is for a setting that exists but cannot be offered here —
 * the switch stays visible and off, because a row that is simply absent
 * gives the reader nowhere to put the reason. Callers that disable a row
 * say why in its `description`.
 */
export function ToggleSettingRow({
  id,
  title,
  description,
  tooltip,
  checked,
  onChange,
  disabled = false,
  indent = false,
}: {
  id: string;
  title: string;
  description?: string;
  tooltip?: string;
  checked: boolean;
  onChange: (next: boolean) => Promise<void>;
  disabled?: boolean;
  /** Dependent setting: indented under its parent row. */
  indent?: boolean;
}) {
  const { saving, error, commit } = useSettingCommit(checked, onChange);

  return (
    <SettingRow
      title={title}
      description={description}
      tooltip={tooltip}
      labelId={id}
      error={error}
      indent={indent}
    >
      {/* aria-label, not aria-labelledby: the Astryx Switch doesn't forward
          labelledby to its input, which left every switch named "Toggle". */}
      <Toggle
        checked={checked}
        disabled={saving || disabled}
        onChange={(next) => void commit(next)}
        aria-label={title}
      />
    </SettingRow>
  );
}

/**
 * Select setting: the shared `Select` in its `quiet` trigger — the value reads
 * as text with a chevron, no boxed input (DESIGN-SYSTEM.md "Selects are quiet").
 * Choosing an option IS the commit; on failure the row shows why.
 */
export function SelectSettingRow({
  id,
  title,
  description,
  tooltip,
  value,
  options,
  onChange,
  indent = false,
}: {
  id: string;
  title: string;
  description?: string;
  tooltip?: string;
  value: string;
  options: SelectOption[];
  onChange: (next: string) => Promise<void>;
  indent?: boolean;
}) {
  const { saving, error, commit } = useSettingCommit(value, onChange);

  return (
    <SettingRow
      title={title}
      description={description}
      tooltip={tooltip}
      htmlFor={id}
      error={error}
      indent={indent}
    >
      <Select
        id={id}
        variant="quiet"
        value={value}
        options={options}
        disabled={saving}
        onChange={(next) => void commit(next)}
        className={saving ? styles.saving : ""}
      />
    </SettingRow>
  );
}
